import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { debounce } from 'redux-saga/effects';

import Const, { TEST_STATUS_ARRAY } from 'constant/Const';
import TrickUtil from 'util/TrickUtil';
import { overFlowBody } from 'util/Utility';
import { gaInitScript } from 'util/TrackerUtility';

import { showDialog } from 'redux/duck/dialogDuck';
import { authLogoutRequested } from 'redux/duck/authDuck';
import { fetchEcgTestsRequested } from 'redux/duck/ecgTestList/ecgTestListDuck';

import useShallowEqualSelector from 'component/hook/useShallowEqualSelector';

import App from 'component/base/App';

const QUERY_PARAM = {
  SEARCH_KEYWORD: 'searchKeyword',
  ECG_TEST_STATUS: 'ecgTestStatus',
  PRESCRIPTION_DURATION: 'prescriptionDuration',
  PAGE: 'page',
  ORDER_BY: 'orderBy',
  START_TIMESTAMP_MS_RANGE: 'startTimestampMsRange',
  END_TIMESTAMP_MS_RANGE: 'endTimestampMsRange',
};

const testStatusArray = TEST_STATUS_ARRAY;
const { SESSION_DURATION_TIME } = Const;

function AppContainer() {
  const dispatch = useDispatch();

  // selector
  const someDialogOpenedState = useShallowEqualSelector(
    (state) => state.dialogReducer.isSomethingOpen
  );
  const authState = useShallowEqualSelector((state) => state.authReducer);

  // dispatch
  const handleShowDialog = (dialogKey, params, callback) => {
    dispatch(showDialog(dialogKey, params, callback));
  };
  const handleLogout = () => dispatch(authLogoutRequested());
  // URL을 토대로 tableFilterOptions 변경
  const handleFetchEcgTestsRequested = (param) => {
    dispatch(
      fetchEcgTestsRequested({
        ...param,
      })
    );
  };

  const favicon = useMemo(
    () => document.querySelectorAll('link[rel=icon]')[0],
    []
  );
  // useEffect
  useEffect(() => {
    if (process.env.REACT_APP_CUSTOM_ENV === 'prod') {
      favicon.href = '/favicon_production/favicon.ico';
    } else if (process.env.REACT_APP_CUSTOM_ENV === 'qa') {
      favicon.href = '/favicon_staging/favicon.ico';
    } else if (process.env.REACT_APP_CUSTOM_ENV === 'dev') {
      favicon.href = '/favicon_dev/favicon.ico';
    } else {
      favicon.href = '/favicon_aux/favicon.ico';
    }
  }, []);

  let timeoutId = null;
  let flag = false;
  const handleEvent = (e) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    if (
      authState.isLoggedIn &&
      !flag &&
      !window.location.pathname.includes('login')
    ) {
      const duration = SESSION_DURATION_TIME * 60 * 60 * 1000;

      timeoutId = setTimeout(() => {
        flag = true;
        handleShowDialog(
          'AlertDialog',
          {
            message: `${SESSION_DURATION_TIME}시간 동안 사용하지 않아 자동 로그아웃 되었습니다.`,
          },
          () => {
            flag = false;
            handleLogout();
          }
        );
      }, duration);
    }
  };

  useEffect(() => {
    TrickUtil.setExpireAlert(() => handleShowDialog('AlertExpireDialog'));
    TrickUtil.setNetworkAlert((errorInfo) =>
      handleShowDialog('AlertNetworkDialog', { errorInfo })
    );
  }, []);

  useEffect(() => {
    someDialogOpenedState ? overFlowBody.hidden() : overFlowBody.auto();
  }, [someDialogOpenedState]);

  useEffect(() => {
    const debouncedHandlerEvent = debounce(handleEvent, 500);
    const handleEventList = ['scroll', 'click', 'mousemove', 'keydown'];

    for (const e of handleEventList) {
      document.addEventListener(e, debouncedHandlerEvent);
    }

    return () => {
      for (const e of handleEventList) {
        document.removeEventListener(e, debouncedHandlerEvent);
      }
    };
  }, [authState.isLoggedIn]);

  // logIn 하지 않은 유저가, URL을 공유받아 들어올 경우, 로그인 페이지로 이동하기 전에 URL을 저장해둔다.
  const initialURL = useRef(window.location.search);
  // URL을 토대로 tableFilterOptions 변경
  useEffect(() => {
    if (!authState.isLoggedIn || window.location.pathname !== '/') return;

    const searchParams = new URLSearchParams(initialURL.current);

    const unfilteredQueryParams = {
      searchKeyword: _getQueryParam(QUERY_PARAM.SEARCH_KEYWORD),
      ecgTestStatus: _getStatusKeys(
        _getArrayQueryParam(QUERY_PARAM.ECG_TEST_STATUS)
      ),
      prescriptionDuration: _getArrayQueryParam(
        QUERY_PARAM.PRESCRIPTION_DURATION,
        String
      ),
      page: Number(_getQueryParam(QUERY_PARAM.PAGE)),
      orderBy: _getQueryParam(QUERY_PARAM.ORDER_BY),
      selectedStartEndDateTime: _getSelectedStartEndDateTime(),
    };

    const queryParams = Object.entries(unfilteredQueryParams).reduce(
      (acc, [key, value]) => {
        if (value && value?.length !== 0) {
          acc[key] = value;
        }
        return acc;
      },
      {}
    );

    handleFetchEcgTestsRequested(queryParams);

    // inner Function
    function _getQueryParam(paramName) {
      return searchParams.get(paramName);
    }
    function _getArrayQueryParam(paramName, parserFunc = Number) {
      const paramValue = _getQueryParam(paramName);
      return paramValue ? paramValue.split(',').map(parserFunc) : undefined;
    }
    function _getSelectedStartEndDateTime() {
      const startTimestampMsRangeParam = searchParams.get(
        QUERY_PARAM.START_TIMESTAMP_MS_RANGE
      );
      const endTimestampMsRangeParam = searchParams.get(
        QUERY_PARAM.END_TIMESTAMP_MS_RANGE
      );

      if (!startTimestampMsRangeParam && !endTimestampMsRangeParam) {
        return undefined;
      }

      const type = startTimestampMsRangeParam
        ? QUERY_PARAM.START_TIMESTAMP_MS_RANGE
        : QUERY_PARAM.END_TIMESTAMP_MS_RANGE;

      return {
        type,
        startTimestampMsRange: _getArrayQueryParam(
          QUERY_PARAM.START_TIMESTAMP_MS_RANGE,
          (timeStamp) => new Date(Number(timeStamp))
        ),
        endTimestampMsRange: _getArrayQueryParam(
          QUERY_PARAM.END_TIMESTAMP_MS_RANGE,
          (timeStamp) => new Date(Number(timeStamp))
        ),
      };
    }
    function _getStatusKeys(statusCodes) {
      if (!statusCodes) return;

      const keySet = new Set();
      for (const statusCode of statusCodes) {
        for (const statusObj of testStatusArray) {
          if (statusObj.value.includes(statusCode)) {
            keySet.add(statusObj.key);
          }
        }
      }
      const keys = Array.from(keySet);
      return keys;
    }
  }, [authState.isLoggedIn]);

  // Google Analytics 실행하는 로직
  useEffect(() => {
    // prod 환경이 아니라면 early return
    if (process.env.REACT_APP_CUSTOM_ENV !== 'prod') return;

    try {
      gaInitScript();
    } catch (error) {
      console.error(error);
    }
  }, []);

  return <App authState={authState} />;
}

export default AppContainer;
